
import { AbstractMapModule, MapLibreLayerConfig, MapModuleStyle, ViewStackController } from "gertrud";
import { sidebarPanelNames } from "@/sidebarPanelNames";
import { MapModuleViewStackItem } from "@/MapModuleViewStackItem";
import { registerMapModule } from "./mapModuleRegistry";
import { AtlasMapModuleConfig } from "atlas-shared/dist/mapApp/mapModuleConfigs/AtlasMapModuleConfig";


/**
 * Extends {@link AbstractMapModule} with a `ViewStackController` reference so
 * that modules can push/pop sidebar panels using the gertrud breadcrumb navigation.
 *
 * In its constructor it also injects the module's MapLibre sources and layers from
 * `moduleConfig` into the shared `mapDef` object (the single MapLibre style object
 * shared across all modules in a map view).
 */

// ATTENTION: Do NOT make this class abstract! It is actually used, for example for base maps!

export class AtlasMapModule extends AbstractMapModule {

    declare moduleConfig: AtlasMapModuleConfig


    /** Layers whose id starts with `<uuid>_hover:` — used for hover highlighting. */
    protected hoverHighlightLayers: MapLibreLayerConfig[] = []

    /** Layers whose id starts with `<uuid>_select:` — used for select highlighting. */
    protected selectHighlightLayers: MapLibreLayerConfig[] = []

    /** The GeoJSON feature property that holds the feature ID. Defaults to `'id'`. Override in subclasses if needed (e.g. `'_id'` for IKM). */
    protected featureIdProperty: string = 'id'

    constructor(config: AtlasMapModuleConfig, protected vsc: ViewStackController) {
        super(config)
    }

    /**
     * Overrides the base `setStyle()` to automatically discover hover/select highlight
     * layers after the style is applied. Subclasses don't need to call
     * `discoverHighlightLayers()` manually — it happens here.
     */
    override setStyle(style: MapModuleStyle): void {
        super.setStyle(style)
        this.discoverHighlightLayers()
    }

    /**
     * Discovers hover and select highlight layers by scanning the active style for
     * layers whose IDs start with `<uuid>_hover:` and `<uuid>_select:` respectively.
     * Call this after every `setStyle()` invocation so the cached layer lists stay
     * in sync with the current style.
     */
    protected discoverHighlightLayers(): void {
        this.hoverHighlightLayers = this.findLayersWithPrefix(this.uuid + "_hover:")
        this.selectHighlightLayers = this.findLayersWithPrefix(this.uuid + "_select:")
    }

    /**
     * Applies a MapLibre `["in", ...]` filter expression to every layer in `layers`.
     *
     * @param layers - The highlight layers to update (hover or select).
     * @param featureIds - The feature IDs to include. Pass an empty array to clear the highlight.
     */
    protected setHighlightFilter(
        layers: MapLibreLayerConfig[],
        featureIds: string[]
    ): void {
        const filter = ["in", ["get", this.featureIdProperty], ["literal", featureIds]]
        for (const layer of layers) {
            this.map?.setFilter(layer.id, filter)
        }
    }


    /**
     * Returns the name of a Vue component to render as this module's quick-settings
     * UI inside the QuickSettingsPanel. Returns `null` to skip rendering a quick
     * settings component for this module (only the visibility toggle will show).
     *
     * The base implementation reads `moduleConfig.quickSettingsComponent` if set,
     * otherwise returns `null`. Override in subclasses to provide module-specific
     * quick settings components programmatically.
     */
    getQuickSettingsComponent(): string | null {
        return null
    }

    /**
     * Pushes this module's root sidebar component onto the ViewStack.
     * Subclasses override this to push their own specific component.
     */
    initSidebar(): void {
        const compName = this.moduleConfig.sidebarRootComponent || sidebarPanelNames.STANDARD_MODULE_MAIN

        const item: MapModuleViewStackItem = {
            componentName: compName,
            props: {
                viewStackController: this.vsc,
                mapModule: this
            },
            role: 'standard-module-details',
            id: '',
            title: this.moduleConfig.title
        }


        //registerMapModule("AtlasMapModule", AtlasMapModule);

        this.vsc.addItem(item)
    }

    /**
     * Called when the user clicks this module's entry in the map-modules sidebar list.
     * Makes the module visible and pushes its root sidebar component onto the ViewStack.
     */
    onModuleListEntryClicked(): void {
        this.visible = true
        this.initSidebar()
    }
}



registerMapModule("AtlasMapModule", AtlasMapModule);